import { Link } from "react-router";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { FolderDto } from "@repo/api/folder";
import { IconDotsVertical, IconFolder } from "@tabler/icons-react";
import { paths } from "@/App";
import { cn } from "@/lib/utils";
import { Button } from "@/lib/components/Button/Button";
import FolderActionsMenu from "./FolderActionsMenu";
import { FolderDropData, folderDropId, VideosDragData } from "./videos-dnd";

type Props = {
  folder: FolderDto;
  teamExternalId: string;
  folderDepth: number;
};

export default function FolderItem({
  folder,
  teamExternalId,
  folderDepth,
}: Props) {
  const {
    listeners,
    setNodeRef: setDraggableNodeRef,
    isDragging,
  } = useDraggable({
    id: `folder:${folder.externalId}`,
    data: { type: "folder", folder } satisfies VideosDragData,
  });

  const {
    setNodeRef: setDroppableNodeRef,
    isOver,
    active,
  } = useDroppable({
    id: folderDropId(folder.externalId),
    data: { folderExternalId: folder.externalId } satisfies FolderDropData,
  });

  const activeDragData = active?.data.current as VideosDragData | undefined;
  const isValidDropTarget =
    !!activeDragData &&
    !(
      activeDragData.type === "folder" &&
      activeDragData.folder.externalId === folder.externalId
    );
  const isDropActive = isOver && isValidDropTarget;

  return (
    <div
      ref={(node) => {
        setDraggableNodeRef(node);
        setDroppableNodeRef(node);
      }}
      {...listeners}
      className={cn(
        "relative flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-white px-4 py-3 transition-colors hover:bg-grey-100",
        !!active && "pointer-events-none",
        isDragging && "opacity-50",
        isDropActive &&
          "border-tertiary-400 bg-tertiary-100 text-tertiary-400 hover:bg-tertiary-100",
      )}
    >
      <Link
        to={paths.videosFolder(teamExternalId, folder.externalId)}
        className="absolute inset-0 rounded-xl"
        aria-label={folder.name}
      />
      <IconFolder
        size={20}
        className={cn(
          "shrink-0 text-text-secondary",
          isDropActive && "text-tertiary-400",
        )}
      />
      <span className="min-w-0 flex-1 truncate font-semibold">
        {folder.name}
      </span>
      <FolderActionsMenu
        folder={folder}
        teamExternalId={teamExternalId}
        folderDepth={folderDepth}
        anchorClassName="relative z-10 shrink-0"
        onAnchorClick={(e) => e.stopPropagation()}
      >
        <Button variant="ghost" size="iconXs" aria-label="Folder options">
          <IconDotsVertical />
        </Button>
      </FolderActionsMenu>
    </div>
  );
}
